import React, { useEffect, useState } from 'react'
import * as queryString from 'query-string'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import NotificationSystem from 'rc-notification'
import { Button } from 'reactstrap'
import { useMediaQuery } from 'react-responsive'
import SpotifyIcon from 'mdi-react/SpotifyIcon'
import { FullWideNotification } from 'Shared/components/Notification'
import { getCookieByName, getUtmParameters } from 'Constants/functions'
import { landingPageUrl } from 'Constants/links'
import logo from 'Shared/img/logo.png'
import closeIconWhite from 'Shared/img/svg/closeWhite.svg'
import closeIconGreen from 'Shared/img/svg/closeGreen.svg'
import { campaignsList, scheduleCampaign } from 'Router/routes'
import { APP } from 'Configs/App'
import Modal from './components/ChooseRoleModal'
import LogInForm from './components/LogInForm'
import { LogInProps } from './LoginProps'
import { Notification, User, UserType } from '../../Types'
import {
  locationEvent,
  loginEvent,
  pageInfo,
  pageViewEvent,
} from '../../Helpers/tagManager'

import { CloseButton } from './styles'

let notification: Notification | null = null

const showNotification = (message: string, color: string) => {
  notification?.notice({
    content: <FullWideNotification color={color} message={message} />,
    position: 'full',
    duration: 3,
    closable: true,
    style: { top: 0, left: 0 },
    className: `ltr-support`,
  })
}

const LogIn: React.FC<LogInProps> = ({
  formStates,
  onSignIn,
  location,
  setRoleToUser,
  history,
  checkUserEmailToSignIn,
  setLoginTypeToInitialState,
  authType,
  sendQuickLoginLink,
  pristine,
  submitting,
  onSignInSpotify,
}) => {
  const { t } = useTranslation()

  const alreadyExistedEmail = localStorage.getItem('alreadyExistedEmail')

  const [loading, setLoading] = useState(false)
  const [modal, setModal] = useState(false)
  const [user, setUser] = useState<User>()
  const toggleModal = () => setModal(!modal)

  useEffect(() => {
    if (history.location.search.indexOf('blacklist') !== -1) {
      history.push('/')
      // eslint-disable-next-line no-shadow
      // @ts-ignore
      NotificationSystem.newInstance({}, notification => {
        notification.notice({
          content: (
            <FullWideNotification
              color="danger"
              message={t('login.authErrorBlacklistRegistered')}
            />
          ),
          position: 'full',
          duration: 5,
          closable: true,
          className: `ltr-support`,
        })
      })
    }
  }, [])

  useEffect(() => {
    locationEvent()
    pageViewEvent(null, pageInfo.Login)

    if (history.location.search.indexOf('user') !== -1) {
      const userData = queryString.parse(location.search)

      let parsedUser = null
      let redirectUrl

      if (
        !isEmpty(userData) &&
        userData.user &&
        typeof userData.user === 'string'
      ) {
        parsedUser = JSON.parse(userData.user)

        if (Object.prototype.hasOwnProperty.call(parsedUser, 'token')) {
          onSignInSpotify(parsedUser)

          loginEvent(parsedUser.user)
          if (parsedUser.schedule) {
            const {
              utm_source,
              utm_campaign,
              utm_content,
              trigger_source,
              trigger_subject,
            } = userData

            if (trigger_source) {
              redirectUrl = `${campaignsList}${scheduleCampaign}/${parsedUser.campaign_id}/?trigger_source=${trigger_source}&trigger_subject=${trigger_subject}&userid=${parsedUser.user.id}`
              history.push(redirectUrl)
            } else {
              redirectUrl = `${campaignsList}${scheduleCampaign}/${parsedUser.campaign_id}/?utm_source=${utm_source}&utm_content=${utm_content}&utm_campaign=${utm_campaign}`
            }
            history.push(redirectUrl)
          } else {
            history.push('/')
          }
        } else {
          setUser(parsedUser)
          setModal(true)
        }
      }
    }

    return () => {
      setLoginTypeToInitialState()
    }
  }, [])

  useEffect(() => {
    if (authType === 'userIsNotExist') {
      setTimeout(() => {
        setLoginTypeToInitialState()
      }, 3000)
    }
  }, [authType])

  const handlelink = () => {
    setLoginTypeToInitialState()
  }

  const handleSendQuickLink = () => {
    formStates.email &&
      sendQuickLoginLink(formStates.email, error => {
        if (
          error === 'Curator in blacklist' ||
          error === 'Country in blacklist'
        ) {
          NotificationSystem.newInstance(
            { style: { top: 65 } },
            (
              // eslint-disable-next-line no-return-assign
              n: Notification | null
            ) => (notification = n)
          )
          showNotification(t('login.authErrorBlacklistRegistered'), 'danger')
          setLoading(false)
        }
        if (!error) {
          NotificationSystem.newInstance(
            { style: { top: 65 } },
            (
              // eslint-disable-next-line no-return-assign
              n: Notification | null
            ) => (notification = n)
          )
          showNotification(t('login.weHaveSent'), 'success')
        }
      })
  }

  const handleSubmit = () => {
    const { email, password } = formStates
    setLoading(true)

    onSignIn(email, password, (error, res) => {
      const errorText =
        error === 'Curator in blacklist' || error === 'Country in blacklist'
          ? t('login.authErrorBlacklistRegistered')
          : t('login.authError')

      if (error) {
        NotificationSystem.newInstance(
          { style: { top: 65 } },
          (
            // eslint-disable-next-line no-return-assign
            n: Notification | null
          ) => (notification = n)
        )
        showNotification(errorText, 'danger')
        setLoading(false)
      } else {
        loginEvent(res.user)
      }
    })
  }

  const handleContinue = () => {
    const { email } = formStates
    checkUserEmailToSignIn(email, error => {
      if (error === 'Curator in blacklist') {
        NotificationSystem.newInstance(
          { style: { top: 65 } },
          (
            // eslint-disable-next-line no-return-assign
            n: Notification | null
          ) => (notification = n)
        )
        showNotification(t('login.authErrorBlacklistRegistered'), 'danger')
        setLoading(false)
      }
    })
  }

  const handleSetRoleToUser = (role: UserType) => {
    const fullGcid = getCookieByName('_ga')
    const gcid = fullGcid ? fullGcid.slice(6) : undefined
    const utmStr = getCookieByName('soundCampaignUtm')
    const utmObj = getUtmParameters(utmStr)

    if (role === 'curator') {
      user &&
        setRoleToUser(
          user.spotify_id,
          role,
          gcid || '',
          utmObj,
          user.access_token,
          user.email,
          user.is_email_confirmed,
          user.refresh_token,
          user.user_photo_url,
          user.first_name,
          user.last_name,
          error => {
            if (
              error === 'Curator in blacklist' ||
              error === 'Country in blacklist'
            ) {
              toggleModal()
              history.push('/')
              NotificationSystem.newInstance(
                { style: { top: 65 } },
                (
                  // eslint-disable-next-line no-return-assign
                  n: Notification | null
                ) => (notification = n)
              )
              showNotification(t('login.authErrorBlacklistCountry'), 'danger')
              setLoading(false)
            }
            if (!error) {
              history.push('/playlists')
            }
          }
        )
    } else {
      user && setRoleToUser(user.spotify_id, role, gcid || '', utmObj)
      history.push('/')
    }
  }

  // eslint-disable-next-line consistent-return
  const handleRenderForm = (type: string) => {
    // eslint-disable-next-line default-case
    switch (type) {
      case 'login':
        return (
          <>
            <LogInForm
              loading={loading}
              data-cy="login-form"
              isContinueButton={authType === 'login'}
              alreadyExistedEmail={alreadyExistedEmail}
              onSubmit={handleContinue}
            />
            <div
              className="account__or"
              data-cy="account__or-login-easily-using"
            >
              <p data-cy="login-form-p">{t('login.easilyUsing')}</p>
            </div>
            <a
              className="btn btn-spotify account__btn account__btn--small"
              data-cy="login-form-sign-in-with-spotify"
              href={APP.SPOTIFY_AUTH_API}
            >
              <SpotifyIcon />
              &nbsp;
              {t('login.signInWithSpotify')}
            </a>
            <div className="text__center">
              <a href={landingPageUrl}>
                {t('login.dontHaveAnAccount')}&#8194;
                <span
                  className="spotify-color"
                  data-cy="login-form-sign-up-link"
                >
                  {t('login.signUp')}
                </span>
              </a>
            </div>
          </>
        )
      case 'regular':
        return (
          <>
            <LogInForm
              loading={loading}
              isContinueButton={authType === 'login'}
              isRegularForm
              initialValues={formStates}
              data-cy="login-form-regular"
              alreadyExistedEmail={alreadyExistedEmail}
              onSubmit={handleSubmit}
            />
            <div
              className="account__or"
              data-cy="account__or-regular-easily-using"
            >
              <p data-cy="login-form-regular-p-easily-using">
                {t('login.easilyUsing')}
              </p>
            </div>
            <span className="text_email">
              <p data-cy="login-form-regular-p-we-can-email">
                {t('login.weCanEmail')}
              </p>
            </span>
            <Button
              className="btn btn-paypal account__btn account__btn--small btn-email"
              data-cy="login-form-quick-login-link"
              onClick={handleSendQuickLink}
            >
              {t('login.emailMe')}
            </Button>
          </>
        )
      case 'spotify':
        return (
          <>
            <LogInForm
              loading={loading}
              initialValues={formStates}
              data-cy="login-form-spotify"
              alreadyExistedEmail={alreadyExistedEmail}
              onSubmit={handleSubmit}
            />
            <span
              className="text_red"
              data-cy="text_red-login-form-spotify-without-password"
            >
              <p data-cy="login-form-spotify-without-password">
                {t('login.withoutPassword')}
              </p>
            </span>
            <span
              className="text_email"
              data-cy="login-form-spotify-we-can-email"
            >
              <p data-cy="login-form-spotify-we-can-email">
                {t('login.weCanEmail')}
              </p>
            </span>
            <Button
              className="btn btn-paypal account__btn account__btn--small btn-email"
              style={{ marginBottom: '20px' }}
              data-cy="login-form-spotify-quick"
              onClick={handleSendQuickLink}
            >
              {t('login.emailMe')}
            </Button>
            <div
              className="account__or"
              data-cy="account__or-spotify-easily-using"
            >
              <p data-cy="login-form-spotify-easily-using">
                {t('login.easilyUsing')}
              </p>
            </div>
            <a
              className="btn btn-success account__btn account__btn--small btn-email"
              data-cy="login-form-spotify-link"
              href={APP.SPOTIFY_AUTH_API}
            >
              <SpotifyIcon />
              &nbsp;
              {t('signup.logInWithSpotify')}
            </a>
            <div
              className="account__or"
              data-cy="account__or-login-easily-using"
            >
              <p data-cy="login-form-p">{t('login.easilyUsing')}</p>
            </div>
            <Button
              type="submit"
              style={{ color: '#ffffff' }}
              className="btn btn-success account__btn account__btn--small"
              data-cy="login-form-sign-another-account"
              disabled={pristine || submitting}
              onClick={handlelink}
            >
              {t('login.logInAnotherAccount')}
            </Button>
          </>
        )
      case 'withoutSignup':
        return (
          <>
            <LogInForm
              loading={loading}
              initialValues={formStates}
              data-cy="login-form-without-signup"
              alreadyExistedEmail={alreadyExistedEmail}
              onSubmit={handleSubmit}
            />
            <span
              className="text_red"
              data-cy="text_red-login-form-p-without-password"
            >
              <p
                data-cy="login-form-p-without-password"
                style={{ marginBottom: 0 }}
              >
                {t('login.withoutPassword')}
              </p>
            </span>
            <span className="text_email">
              <p data-cy="login-form-p-without-we-can-email">
                {t('login.weCanEmail')}
              </p>
            </span>
            <Button
              className="btn btn-paypal account__btn account__btn--small btn-email"
              data-cy="login-form-quick-without-signup"
              onClick={handleSendQuickLink}
            >
              {t('login.emailMe')}
            </Button>
            <div
              className="account__or"
              data-cy="account__or-login-easily-using"
            >
              <p data-cy="login-form-p">{t('login.easilyUsing')}</p>
            </div>
            <Button
              type="submit"
              style={{ color: '#ffffff' }}
              className="btn btn-success account__btn account__btn--small"
              data-cy="login-form-sign-another-account"
              disabled={pristine || submitting}
              onClick={handlelink}
            >
              {t('login.logInAnotherAccount')}
            </Button>
          </>
        )
      case 'userIsNotExist':
        return (
          <>
            <LogInForm
              loading={loading}
              initialValues={formStates}
              data-cy="login-form-user-not-exist"
              alreadyExistedEmail={alreadyExistedEmail}
              onSubmit={handleSubmit}
            />
            <span
              className="text_red"
              data-cy="text_red-login-form-user-not-exist-no-account"
            >
              <p
                data-cy="login-form-user-not-exist-no-account"
                style={{ marginBottom: 0 }}
              >
                {t('login.noAccount')}
              </p>
            </span>
            <span className="text_email">
              <p data-cy="login-form-user-not-exist-check">
                {t('login.checkIf')}
              </p>
            </span>
            <div
              className="account__or"
              data-cy="account__or-user-not-exist-easily-using"
            >
              <p data-cy="login-form-user-not-exist-easily-using">
                {t('login.easilyUsing')}
              </p>
            </div>
            <span className="text_email">
              <p data-cy="login-form-user-not-exist-create-new">
                {t('login.createANew')}
              </p>
            </span>
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              <div>
                <button
                  type="button"
                  className="btn-success btn"
                  data-cy="login-form-btn-without-signup-artist"
                  onClick={() => history.push('/create')}
                >
                  {t('login.artist')}
                </button>
              </div>
              <div>
                <button
                  type="button"
                  className="btn-success btn"
                  data-cy="login-form-btn-without-signup-curator"
                  onClick={() => history.push('/signup-curator')}
                >
                  {t('login.curator')}
                </button>
              </div>
            </div>
          </>
        )
    }
  }

  const onGoToLanding = () => {
    window.location.href = landingPageUrl
  }

  const isMobile = useMediaQuery({ maxWidth: 576 })

  return (
    <div className="account">
      <CloseButton
        src={isMobile ? closeIconGreen : closeIconWhite}
        onClick={onGoToLanding}
      />
      <Modal
        handleSubmit={handleSetRoleToUser}
        modalIsOpen={modal}
        toggle={toggleModal}
        history={history}
      />
      <div className="account__wrapper">
        <div className="login-logo">
          <img alt="logo" src={logo} />
        </div>
        <div className="account__card" data-cy="account__card">
          <div className="account__head">
            <h3>{t('login.welcomeBack')}</h3>
          </div>
          {handleRenderForm(authType)}
        </div>
      </div>
    </div>
  )
}

export default LogIn
